/**
 * What needs a person today, and what is coming.
 *
 * Every tile is a link to the list behind it, and every figure is derived on
 * the read from the same scoped query that list uses — so a tile can never
 * disagree with the screen it opens. What each role sees is what that role may
 * see: the backend scopes it, and this screen shows whatever comes back rather
 * than keeping a second idea of who sees what.
 *
 * Under the tiles: what falls due, what would stop the month closing, and the
 * role-specific pieces that are not about work waiting on somebody.
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr, type ChainHead, type Period, type TrialBalance } from '../api'
import { ROLE_LABELS, useAuth } from '../auth'
import { Card, Empty, ErrorNote, Loading, Note, Pill, Stat } from '../components/ui'
import { CashPosition } from '../components/CashPosition'
import { CategoryMovement } from '../components/CategoryMovement'

interface Tile {
  key: string
  label: string
  count: number
  amount_paise: number
  link: string
  detail: string
  urgent: boolean
}

interface DueItem {
  kind: string
  reference: string
  what: string
  due_on: string
  amount_paise: number
  raised: boolean
  link: string
}

interface CloseCheck {
  key: string
  title: string
  detail: string
  count: number
}

interface DashboardData {
  as_at: string
  tiles: Tile[]
  due_soon: {
    days: number
    items: DueItem[]
    total_paise: number
    overdue_paise: number
    overdue_count: number
  }
  exceptions: { kind: string; severity: string; count: number; amount_paise: number }[]
  close: {
    period: string | null
    period_end?: string
    blocking: CloseCheck[]
    advisory: CloseCheck[]
    ready: boolean
  }
}

const DUE_LABELS: Record<string, string> = {
  commitment: 'Commitment',
  loan: 'Loan instalment',
  loan_receivable: 'Owed to us',
  payment_request: 'Payment request',
}

function TileCard({ tile }: { tile: Tile }) {
  return (
    <Link className="card stat" to={tile.link} style={{ textDecoration: 'none' }}>
      <div className="label">{tile.label}</div>
      <div className="value">
        {tile.count}
        {tile.amount_paise > 0 && (
          <span className="faint" style={{ fontSize: 14 }}>
            {' '}
            · {formatInr(tile.amount_paise)}
          </span>
        )}
      </div>
      <div className="note">{tile.detail}</div>
    </Link>
  )
}

function DueSoon({ data }: { data: DashboardData['due_soon'] }) {
  if (data.items.length === 0) return <Empty>Nothing falls due in the next {data.days} days.</Empty>
  return (
    <>
      {data.overdue_count > 0 && (
        <Note kind="warn">
          {data.overdue_count} of these are already past their date, worth{' '}
          {formatInr(data.overdue_paise)}.
        </Note>
      )}
      <table>
        <thead>
          <tr>
            <th>Due</th>
            <th>What</th>
            <th>Reference</th>
            <th className="num">Amount</th>
            <th>Raised</th>
          </tr>
        </thead>
        <tbody>
          {data.items.slice(0, 15).map((item, index) => (
            <tr key={`${item.reference}-${item.due_on}-${index}`}>
              <td className="muted date">{item.due_on}</td>
              <td>
                <Link to={item.link}>{item.what}</Link>
                <div className="faint" style={{ fontSize: 12 }}>
                  {DUE_LABELS[item.kind] ?? item.kind}
                </div>
              </td>
              <td className="mono">{item.reference}</td>
              <td className="num">{formatInr(item.amount_paise)}</td>
              <td className="muted">
                {item.raised ? 'yes' : <span className="faint">not yet</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.items.length > 15 && (
        <p className="faint" style={{ marginBottom: 0 }}>
          and {data.items.length - 15} more, worth {formatInr(data.total_paise)} in all.
        </p>
      )}
    </>
  )
}

function TheClose({ close }: { close: DashboardData['close'] }) {
  if (!close.period) return <Note kind="warn">No month is open.</Note>
  if (close.ready)
    return (
      <Note kind="ok">
        {close.period} has nothing blocking it. The checklist runs again when somebody actually
        closes it.
      </Note>
    )
  return (
    <>
      <p className="muted" style={{ marginTop: 0 }}>
        {close.period} cannot close until these are dealt with.
      </p>
      <table>
        <thead>
          <tr>
            <th>Check</th>
            <th>What is wrong</th>
            <th className="num">Rows</th>
          </tr>
        </thead>
        <tbody>
          {close.blocking.map((check) => (
            <tr key={check.key}>
              <td>{check.title}</td>
              <td className="muted">{check.detail}</td>
              <td className="num">{check.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="faint" style={{ marginBottom: 0 }}>
        <Link to={`/books/periods/${close.period}`}>Open the full checklist</Link>
        {close.advisory.length > 0 &&
          ` — ${close.advisory.length} further check(s) are worth looking at but do not block.`}
      </p>
    </>
  )
}

/** The tiles, what is due, and the month — scoped, so the same for every role. */
function Work() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardData>('/dashboard'),
  })
  if (isLoading) return <Loading what="Working out what needs you" />
  if (error) return <ErrorNote error={error} />
  if (!data) return null

  return (
    <>
      <div className="grid">
        {data.tiles.map((tile) => (
          <TileCard key={tile.key} tile={tile} />
        ))}
      </div>
      <Card
        title={`Due in the next ${data.due_soon.days} days`}
        hint="commitments, loan instalments and approved payments, in one list"
        tight
      >
        <DueSoon data={data.due_soon} />
      </Card>
      {data.exceptions.length > 0 && (
        <Card title="Open exceptions" hint="raised by checks that run again" tight>
          <table>
            <thead>
              <tr>
                <th>Kind</th>
                <th>Severity</th>
                <th className="num">Count</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.exceptions.map((row) => (
                <tr key={`${row.kind}-${row.severity}`}>
                  <td>
                    <Link to="/control/exceptions">{row.kind.replace(/_/g, ' ')}</Link>
                  </td>
                  <td>
                    <Pill value={row.severity} />
                  </td>
                  <td className="num">{row.count}</td>
                  <td className="num">{formatInr(row.amount_paise)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Card title="The month" tight>
        <TheClose close={data.close} />
      </Card>
    </>
  )
}

function OpenPeriods() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['periods'],
    queryFn: () => api.get<Period[]>('/periods'),
  })
  if (isLoading) return <Loading what="Loading periods" />
  if (error) return <ErrorNote error={error} />
  const open = (data ?? []).filter((p) => p.status === 'open' || p.status === 'under_review')
  if (open.length === 0)
    return (
      <Note kind="warn">
        No month is open. Open a financial year before anything can be posted.
      </Note>
    )
  return (
    <table>
      <thead>
        <tr>
          <th>Month</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {open.map((p) => (
          <tr key={p.period_label}>
            <td>{p.period_label}</td>
            <td>
              <Pill value={p.status} />
            </td>
            <td className="num">
              <Link to={`/books/periods/${p.period_label}`}>Checklist</Link>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function FinanceHeadExtras() {
  const { data: tb } = useQuery({
    queryKey: ['tb', 'today'],
    queryFn: () =>
      api.get<TrialBalance>(
        `/reports/trial-balance?as_at=${new Date().toISOString().slice(0, 10)}`,
      ),
  })
  return (
    <>
      <div className="grid">
        <Stat
          label="Books balance"
          value={tb ? (tb.balanced ? 'Yes' : 'No') : '—'}
          note={
            tb && !tb.balanced
              ? `out by ${formatInr(tb.difference_paise)} — this is a defect, not a decision`
              : 'debits equal credits'
          }
        />
      </div>
      <CashPosition />
      <CategoryMovement />
      <Card title="Reading the money" tight>
        <p className="muted" style={{ margin: 0 }}>
          A loan received and a month's fees both add to the bank, and only one of them is revenue.{' '}
          <Link to="/books/money-flows">Money in &amp; out</Link> keeps them apart.
        </p>
      </Card>
      <Card title="Months you can close" hint="the checklist is derived, never cached" tight>
        <OpenPeriods />
      </Card>
    </>
  )
}

function AdminExtras() {
  const { data: chain, error } = useQuery({
    queryKey: ['chain'],
    queryFn: () => api.get<ChainHead>('/audit/chain'),
  })
  return (
    <>
      <ErrorNote error={error} />
      <div className="grid">
        <Stat
          label="Audit entries"
          value={chain?.head_seq ?? '—'}
          note="append-only; no one can edit these"
        />
        <Stat
          label="Unanchored history"
          value={chain?.rows_since_anchor ?? '—'}
          note={
            chain?.anchored_at
              ? `last copied off-box ${chain.anchored_at.slice(0, 10)}`
              : 'never copied off-box yet'
          }
        />
      </div>
      <CashPosition />
      <CategoryMovement />
      <Card title="What you can do here">
        <p className="muted" style={{ marginBottom: 0 }}>
          You configure the organisation and can read everything — including the cash position
          above. You cannot create, approve or release a financial transaction: the role holds no
          transactional permission at all, which is what makes the audit trail meaningful rather
          than merely present.
        </p>
      </Card>
    </>
  )
}

function BookKeeperExtras() {
  return (
    <Card title="Months in progress" tight>
      <OpenPeriods />
    </Card>
  )
}

export default function Dashboard() {
  const { me } = useAuth()
  if (!me) return null

  const extras =
    me.role === 'admin' ? (
      <AdminExtras />
    ) : me.role === 'finance_head' ? (
      <FinanceHeadExtras />
    ) : me.role === 'book_keeper' ? (
      <BookKeeperExtras />
    ) : null

  return (
    <>
      <p className="muted">
        Signed in as <strong>{me.display_name}</strong> ({ROLE_LABELS[me.role] ?? me.role}) —{' '}
        {me.permissions.length} permissions, visibility <code>{me.visibility}</code>.
      </p>
      <Work />
      {extras}
    </>
  )
}
